import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import AppDialog from "../../shared/ui/AppDialog";
import { useNotify } from "../../shared/ui/feedback/SnackbarProvider";
import { holidayErrorMessage } from "./errors";
import { useHolidayMutations } from "./hooks";
import { Holiday, LIMITS } from "./types";

export type DeactivateHolidayDialogProps = { holiday: Holiday | null; onClose: () => void };

/** Desativar um feriado municipal: motivo obrigatório (auditado no BE). */
export default function DeactivateHolidayDialog({ holiday, onClose }: DeactivateHolidayDialogProps) {
  const notify = useNotify();
  const { deactivate } = useHolidayMutations();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setReason("");
    setError(null);
  }, [holiday?.id]);

  if (!holiday) return null;
  const valid = reason.trim().length >= LIMITS.reason[0];

  const confirm = async () => {
    setError(null);
    try {
      await deactivate.mutateAsync({ id: holiday.id, reason: reason.trim() });
      notify({ severity: "success", message: `Feriado “${holiday.name}” desativado.` });
      onClose();
    } catch (e) {
      setError(holidayErrorMessage(e, "Não foi possível desativar o feriado. Tente de novo."));
    }
  };

  return (
    <AppDialog
      open
      onClose={onClose}
      title="Desativar feriado"
      context={holiday.name}
      busy={deactivate.isPending}
      actions={
        <>
          <Button onClick={onClose} disabled={deactivate.isPending} sx={{ color: "text.primary" }}>
            Voltar
          </Button>
          <LoadingButton variant="contained" color="error" loading={deactivate.isPending} disabled={!valid} onClick={confirm}>
            Desativar feriado
          </LoadingButton>
        </>
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography>O feriado deixa de ajustar o “esperado” da sazonalidade. Dá para reativar depois.</Typography>
        <TextField label="Motivo" value={reason} onChange={(e) => setReason(e.target.value)} required multiline minRows={2} inputProps={{ maxLength: LIMITS.reason[1] }} />
      </Stack>
    </AppDialog>
  );
}
